import {
  LAUNCH_FEE_WEI,
  SIM_WALLET_START_WEI,
} from '@/lib/launchpad/constants'
import {
  buyOnCurve,
  buyOnPool,
  claimCreatorFees,
  createLaunchState,
  sellOnCurve,
  sellOnPool,
} from '@/lib/launchpad/curveMath'
import { deserializeLaunch, serializeLaunch } from '@/lib/launchpad/serialize'
import type { SerializedLaunch } from '@/lib/launchpad/serialize'
import type { BuyResult, LaunchState, SellResult } from '@/lib/launchpad/types'
import { optionalTokenSocials, type TokenSocials } from '@/lib/tokenSocials'
import { jsonToBigint } from '@/lib/launchpad/wei'
import { isHiddenLaunch } from '@/lib/hiddenListings'
import { applyLiveListingReset, isAfterLiveReset } from '@/lib/liveReset'
import { persistedShareSlugs, uniqueShareSlug } from '@/lib/shareSlug'
import { assertCreatorAllowed, assertPenguinPadBrandAllowed } from '@/lib/createGuard'
import { reportTradeActivity } from '@/lib/tradeActivity'

const LAUNCHES_KEY = 'penguin-pad-sim-launches'
const WALLET_KEY = 'penguin-pad-sim-eth'
const HOLDINGS_KEY = 'penguin-pad-sim-holdings'
const WALLETS_KEY = 'penguin-pad-sim-eth-by-wallet'
const HOLDINGS_BY_WALLET_KEY = 'penguin-pad-sim-holdings-by-wallet'
const STORE_EVENT = 'penguin-pad-sim-launchpad'
export const LEGACY_SIM_WALLET = 'sim-wallet'

type WalletHoldings = Record<string, string>
type HoldingsByWallet = Record<string, WalletHoldings>
type WalletsByAddress = Record<string, string>

interface PersistedStore {
  launches: SerializedLaunch[]
  wallets: WalletsByAddress
  holdings: HoldingsByWallet
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function emptyStore(): PersistedStore {
  return {
    launches: [],
    wallets: {},
    holdings: {},
  }
}

export function normalizeWalletKey(address: string | undefined): string {
  const trimmed = address?.trim().toLowerCase() ?? ''
  return trimmed || LEGACY_SIM_WALLET
}

function isHoldingsByWallet(raw: unknown): raw is HoldingsByWallet {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw))
    return false
  return Object.values(raw as Record<string, unknown>).every((value) => {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
  })
}

function readJson<T>(key: string): T | undefined {
  if (!canUseStorage())
    return undefined
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw)
      return undefined
    return JSON.parse(raw) as T
  }
  catch {
    return undefined
  }
}

function migrateWallets(): WalletsByAddress {
  const next = readJson<WalletsByAddress>(WALLETS_KEY)
  if (next && typeof next === 'object' && !Array.isArray(next))
    return next
  const legacy = canUseStorage() ? window.localStorage.getItem(WALLET_KEY) : null
  if (legacy && /^-?\d+$/.test(legacy))
    return { [LEGACY_SIM_WALLET]: legacy }
  return {}
}

function migrateHoldings(): HoldingsByWallet {
  const next = readJson<HoldingsByWallet>(HOLDINGS_BY_WALLET_KEY)
  if (isHoldingsByWallet(next))
    return next
  const legacy = readJson<WalletHoldings>(HOLDINGS_KEY)
  if (legacy && typeof legacy === 'object' && !Array.isArray(legacy) && !isHoldingsByWallet(legacy))
    return { [LEGACY_SIM_WALLET]: legacy }
  return {}
}

function readStore(): PersistedStore {
  applyLiveListingReset()
  if (!canUseStorage())
    return emptyStore()
  try {
    const launchesRaw = window.localStorage.getItem(LAUNCHES_KEY)
    const launches = launchesRaw ? JSON.parse(launchesRaw) as SerializedLaunch[] : []
    return {
      launches: Array.isArray(launches) ? launches : [],
      wallets: migrateWallets(),
      holdings: migrateHoldings(),
    }
  }
  catch {
    return emptyStore()
  }
}

function writeStore(next: PersistedStore) {
  if (!canUseStorage())
    return
  try {
    window.localStorage.setItem(LAUNCHES_KEY, JSON.stringify(next.launches))
    window.localStorage.setItem(WALLETS_KEY, JSON.stringify(next.wallets))
    window.localStorage.setItem(HOLDINGS_BY_WALLET_KEY, JSON.stringify(next.holdings))
    window.dispatchEvent(new Event(STORE_EVENT))
  }
  catch {
    // Ignore quota / private-mode failures in this simulated UI.
  }
}

function notify() {
  if (typeof window !== 'undefined')
    window.dispatchEvent(new Event(STORE_EVENT))
}

export function subscribeSimLaunchpad(listener: () => void): () => void {
  if (typeof window === 'undefined')
    return () => {}
  const onStorage = (event: StorageEvent) => {
    if (
      event.key === LAUNCHES_KEY
      || event.key === WALLET_KEY
      || event.key === HOLDINGS_KEY
      || event.key === WALLETS_KEY
      || event.key === HOLDINGS_BY_WALLET_KEY
    ) {
      listener()
    }
  }
  window.addEventListener(STORE_EVENT, listener)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(STORE_EVENT, listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function listSimLaunches(): LaunchState[] {
  return readStore().launches
    .map(deserializeLaunch)
    .filter(launch => isAfterLiveReset(launch.createdAtMs) && !isHiddenLaunch(launch))
}

export function getSimLaunch(id: string): LaunchState | undefined {
  const key = id.trim().toLowerCase()
  const raw = readStore().launches.find((item) => {
    return item.id === id
      || item.id.toLowerCase() === key
      || item.contractAddress?.toLowerCase() === key
      || item.shareSlug?.toLowerCase() === key
  })
  if (!raw)
    return undefined
  const launch = deserializeLaunch(raw)
  if (!isAfterLiveReset(launch.createdAtMs) || isHiddenLaunch(launch))
    return undefined
  return launch
}

export function updateSimLaunchImage(id: string, imageUrl: string) {
  const nextUrl = imageUrl.trim()
  if (!id.trim() || !nextUrl)
    return
  const store = readStore()
  const key = id.trim().toLowerCase()
  const index = store.launches.findIndex((item) => {
    return item.id === id
      || item.id.toLowerCase() === key
      || item.contractAddress?.toLowerCase() === key
      || item.shareSlug?.toLowerCase() === key
  })
  if (index < 0)
    return
  const launch = deserializeLaunch(store.launches[index])
  if (launch.imageUrl === nextUrl)
    return
  saveLaunch({ ...launch, imageUrl: nextUrl }, store.wallets, store.holdings)
}

export function upsertOnChainLaunch(state: LaunchState) {
  const store = readStore()
  const key = state.contractAddress?.trim().toLowerCase()
  const index = store.launches.findIndex((item) => {
    return item.id === state.id
      || (key && item.id.toLowerCase() === key)
      || (key && item.contractAddress?.toLowerCase() === key)
  })
  const existingSlug = index >= 0 ? store.launches[index]?.shareSlug : state.shareSlug
  const shareSlug = uniqueShareSlug(state.name, persistedShareSlugs(existingSlug), existingSlug)
  const serialized = serializeLaunch({ ...state, shareSlug })
  const launches = index >= 0
    ? store.launches.map((item, i) => (i === index ? serialized : item))
    : [serialized, ...store.launches]
  writeStore({
    ...store,
    launches,
  })
}

function walletBalance(store: PersistedStore, walletKey: string): bigint {
  const raw = store.wallets[walletKey]
  if (raw && /^-?\d+$/.test(raw))
    return jsonToBigint(raw)
  return SIM_WALLET_START_WEI
}

export function getSimWalletWei(address?: string): bigint {
  if (!address)
    return 0n
  return walletBalance(readStore(), normalizeWalletKey(address))
}

export function getSimHolding(launchId: string, address?: string): bigint {
  if (!address)
    return 0n
  const walletKey = normalizeWalletKey(address)
  return jsonToBigint(readStore().holdings[walletKey]?.[launchId] ?? '0')
}

export function getSimHoldingForLaunch(
  launch: { id: string, contractAddress?: string },
  address?: string,
): bigint {
  if (!address)
    return 0n
  const walletKey = normalizeWalletKey(address)
  const holdings = readStore().holdings[walletKey] ?? {}
  const keys = new Set(
    [launch.id, launch.contractAddress, launch.id.toLowerCase(), launch.contractAddress?.toLowerCase()]
      .filter((value): value is string => Boolean(value)),
  )
  let amount = 0n
  for (const key of keys) {
    const next = jsonToBigint(holdings[key] ?? '0')
    if (next > amount)
      amount = next
  }
  return amount
}

export function getSimHoldings(address?: string): Record<string, bigint> {
  if (!address)
    return {}
  const walletKey = normalizeWalletKey(address)
  const raw = readStore().holdings[walletKey] ?? {}
  const next: Record<string, bigint> = {}
  for (const [id, value] of Object.entries(raw)) {
    const amount = jsonToBigint(value)
    if (amount > 0n)
      next[id] = amount
  }
  return next
}

export function getSimHoldersForLaunch(launch: { id: string, contractAddress?: string }): { address: string, amount: bigint }[] {
  const keys = new Set(
    [launch.id, launch.contractAddress, launch.id.toLowerCase(), launch.contractAddress?.toLowerCase()]
      .filter((value): value is string => Boolean(value)),
  )
  const rows: { address: string, amount: bigint }[] = []
  for (const [wallet, holdings] of Object.entries(readStore().holdings)) {
    if (!wallet || wallet === LEGACY_SIM_WALLET)
      continue
    let amount = 0n
    for (const key of keys) {
      const next = jsonToBigint(holdings?.[key] ?? '0')
      if (next > amount)
        amount = next
    }
    if (amount > 0n)
      rows.push({ address: wallet, amount })
  }
  return rows
}

function saveLaunch(state: LaunchState, wallets: WalletsByAddress, holdings: HoldingsByWallet) {
  const store = readStore()
  const serialized = serializeLaunch(state)
  const index = store.launches.findIndex(item => item.id === state.id)
  const launches = index >= 0
    ? store.launches.map((item, i) => (i === index ? serialized : item))
    : [serialized, ...store.launches]
  writeStore({
    launches,
    wallets,
    holdings,
  })
}

export interface CreateSimLaunchInput {
  name: string
  ticker: string
  imageUrl: string
  socials?: TokenSocials
  creatorAddress: string
  id?: string
  contractAddress?: string
  chargeSimFee?: boolean
  graduationQuote?: bigint
}

export function createSimLaunch(input: CreateSimLaunchInput): LaunchState {
  const store = readStore()
  const walletKey = normalizeWalletKey(input.creatorAddress)
  const wallet = walletBalance(store, walletKey)
  const chargeSimFee = input.chargeSimFee !== false
  if (chargeSimFee && wallet < LAUNCH_FEE_WEI)
    throw new Error('Sim wallet cannot cover the 0.00025 ETH launch fee')

  const ticker = input.ticker.trim().toUpperCase()
  const name = input.name.trim()
  const creatorAddress = input.creatorAddress.trim()
  if (!name || !ticker)
    throw new Error('Name and ticker are required')
  if (!creatorAddress)
    throw new Error('Connect a wallet before launching')
  assertCreatorAllowed(creatorAddress)
  assertPenguinPadBrandAllowed(name, ticker, creatorAddress)

  const id = input.id?.trim() || `${ticker.toLowerCase()}-${Date.now().toString(36)}`
  const shareSlug = uniqueShareSlug(name, persistedShareSlugs())
  const state = createLaunchState({
    id,
    name,
    ticker,
    imageUrl: input.imageUrl.trim(),
    socials: optionalTokenSocials(input.socials),
    creatorAddress,
    createdAtMs: Date.now(),
    contractAddress: input.contractAddress,
    graduationQuote: input.graduationQuote,
    shareSlug,
  })

  const wallets = { ...store.wallets }
  wallets[walletKey] = (chargeSimFee ? wallet - LAUNCH_FEE_WEI : wallet).toString()

  writeStore({
    launches: [serializeLaunch(state), ...store.launches.filter(item => item.id !== id)],
    wallets,
    holdings: store.holdings,
  })
  notify()
  return state
}

export function buySimLaunch(
  id: string,
  quoteIn: bigint,
  nowMs = Date.now(),
  address?: string,
  options?: { chargeSimQuote?: boolean },
): { state: LaunchState, result: BuyResult } {
  if (!address?.trim())
    throw new Error('Connect a wallet before trading')
  const store = readStore()
  const current = store.launches.find(item => item.id === id)
  if (!current)
    throw new Error('Launch not found')
  const walletKey = normalizeWalletKey(address)
  const wallet = walletBalance(store, walletKey)
  const chargeSimQuote = options?.chargeSimQuote !== false
  if (chargeSimQuote && quoteIn > wallet)
    throw new Error('Sim wallet does not have enough ETH')

  const launch = deserializeLaunch(current)
  const traded = launch.status === 'graduated'
    ? buyOnPool(launch, quoteIn)
    : buyOnCurve(launch, quoteIn, nowMs)

  if (chargeSimQuote && traded.result.quoteSpent > wallet)
    throw new Error('Sim wallet does not have enough ETH')

  const holdings = { ...store.holdings }
  const walletHoldings = { ...(holdings[walletKey] ?? {}) }
  const nextHolding = jsonToBigint(walletHoldings[id] ?? '0') + traded.result.tokensOut
  walletHoldings[id] = nextHolding.toString()
  holdings[walletKey] = walletHoldings

  const wallets = { ...store.wallets }
  wallets[walletKey] = (chargeSimQuote ? wallet - traded.result.quoteSpent : wallet).toString()
  saveLaunch(traded.state, wallets, holdings)
  reportTradeActivity({
    projectKey: traded.state.contractAddress || traded.state.id,
    kind: 'curve',
    wallet: address,
    side: 'buy',
    tokenAmount: traded.result.tokensOut.toString(),
    quoteAmount: traded.result.quoteSpent.toString(),
  })
  return traded
}

export function sellSimLaunch(
  id: string,
  tokenIn: bigint,
  address?: string,
  options?: { creditSimQuote?: boolean },
): { state: LaunchState, result: SellResult } {
  if (!address?.trim())
    throw new Error('Connect a wallet before trading')
  const store = readStore()
  const current = store.launches.find(item => item.id === id)
  if (!current)
    throw new Error('Launch not found')
  const walletKey = normalizeWalletKey(address)
  const holding = jsonToBigint(store.holdings[walletKey]?.[id] ?? '0')
  if (tokenIn > holding)
    throw new Error('Not enough tokens to sell')

  const launch = deserializeLaunch(current)
  const traded = launch.status === 'graduated'
    ? sellOnPool(launch, tokenIn)
    : sellOnCurve(launch, tokenIn)

  const holdings = { ...store.holdings }
  const walletHoldings = { ...(holdings[walletKey] ?? {}) }
  walletHoldings[id] = (holding - tokenIn).toString()
  holdings[walletKey] = walletHoldings
  const wallets = { ...store.wallets }
  const wallet = walletBalance(store, walletKey)
  const creditSimQuote = options?.creditSimQuote !== false
  wallets[walletKey] = (creditSimQuote ? wallet + traded.result.userReceives : wallet).toString()
  saveLaunch(traded.state, wallets, holdings)
  reportTradeActivity({
    projectKey: traded.state.contractAddress || traded.state.id,
    kind: 'curve',
    wallet: address,
    side: 'sell',
    tokenAmount: tokenIn.toString(),
    quoteAmount: traded.result.userReceives.toString(),
  })
  return traded
}

export function claimSimCreatorFees(
  id: string,
  address?: string,
  options?: { creditSimQuote?: boolean },
): { state: LaunchState, amount: bigint } {
  if (!address?.trim())
    throw new Error('Connect a wallet before claiming creator fees')
  const store = readStore()
  const current = store.launches.find(item => item.id === id)
  if (!current)
    throw new Error('Launch not found')

  const launch = deserializeLaunch(current)
  const claimed = claimCreatorFees(launch, address)
  const walletKey = normalizeWalletKey(address)
  const wallets = { ...store.wallets }
  const wallet = walletBalance(store, walletKey)
  const creditSimQuote = options?.creditSimQuote !== false
  wallets[walletKey] = (creditSimQuote ? wallet + claimed.amount : wallet).toString()
  saveLaunch(claimed.state, wallets, store.holdings)
  return claimed
}

export function resetSimWalletToDefault(address?: string) {
  const store = readStore()
  const wallets = { ...store.wallets }
  wallets[normalizeWalletKey(address)] = SIM_WALLET_START_WEI.toString()
  writeStore({
    ...store,
    wallets,
  })
}

export function adjustSimWalletWei(address: string, deltaWei: bigint): bigint {
  if (!address?.trim())
    throw new Error('Connect a wallet first')
  const store = readStore()
  const walletKey = normalizeWalletKey(address)
  const wallet = walletBalance(store, walletKey)
  const next = wallet + deltaWei
  if (next < 0n)
    throw new Error('Sim wallet does not have enough ETH')
  const wallets = { ...store.wallets }
  wallets[walletKey] = next.toString()
  writeStore({
    ...store,
    wallets,
  })
  return next
}
